import { useState } from 'react';

import { Footer, FooterActions, Unsaved } from './styles';

import Button from '@/components/Button';
import ConfirmationModal from '@/components/ConfirmationModalV2';
import Modal from '@/components/Modal';
import PermissionComponent from '@/routes/PermissionComponent';

type Props = {
  label: string;
  readOnly: boolean;
  busy: boolean;
  dirty: boolean;
  hasDraft: boolean;
  hasProblems: boolean;
  saveBlocked: boolean;
  onSave: () => void;
  onPublish: () => void;
  onDiscard: () => void;
  onReset: () => Promise<unknown>;
};

const guarded = (readOnly: boolean, title?: string) =>
  readOnly ? 'Tenant admin only' : title;

const CopyActions = ({
  label,
  readOnly,
  busy,
  dirty,
  hasDraft,
  hasProblems,
  saveBlocked,
  onSave,
  onPublish,
  onDiscard,
  onReset,
}: Props) => {
  const [confirming, setConfirming] = useState(false);
  // Same "Discard unsaved changes?" question Revert asks (CopyEditor).
  const [guard, setGuard] = useState<'reset' | null>(null);
  // The saved draft is shared and not kept in History: Discard deleted another
  // admin's draft with one click (review D-06), so it always asks, once, and
  // says when unsaved edits go too.
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const publishBlocked =
    readOnly || busy || hasProblems || (!dirty && !hasDraft);

  const confirmReset = async () => {
    await onReset();
    setConfirming(false);
  };

  const startReset = () => (dirty ? setGuard('reset') : setConfirming(true));
  const startDiscard = () => setConfirmDiscard(true);
  const passGuard = () => {
    setGuard(null);
    setConfirming(true);
  };

  return (
    <Footer>
      {dirty && <Unsaved>Unsaved changes</Unsaved>}
      <FooterActions data-testid="copyFooterActions">
        <span title={guarded(readOnly)}>
          <PermissionComponent id="btn-lifecycle-copy-publish" showAsReadOnly>
            <Button
              a11yLabel="Reset to default"
              label="Reset to default"
              appearance="outline"
              data-testid="copyResetBtn"
              disabled={readOnly || busy}
              onClick={startReset}
            />
          </PermissionComponent>
        </span>
        {hasDraft && (
          <span title={guarded(readOnly)}>
            <PermissionComponent id="btn-lifecycle-copy-publish" showAsReadOnly>
              <Button
                a11yLabel="Discard draft"
                label="Discard draft"
                appearance="outline"
                data-testid="copyDiscardBtn"
                disabled={readOnly || busy}
                onClick={startDiscard}
              />
            </PermissionComponent>
          </span>
        )}
      </FooterActions>
      <span title={guarded(readOnly)}>
        <PermissionComponent id="btn-lifecycle-copy-publish" showAsReadOnly>
          <Button
            a11yLabel="Save draft"
            label="Save draft"
            appearance="outline"
            data-testid="copySaveDraftBtn"
            disabled={saveBlocked}
            onClick={onSave}
          />
        </PermissionComponent>
      </span>
      <span
        title={guarded(
          readOnly,
          hasProblems ? 'Fix the problems above first' : undefined,
        )}
      >
        <PermissionComponent id="btn-lifecycle-copy-publish" showAsReadOnly>
          <Button
            a11yLabel="Publish"
            label="Publish"
            appearance="primary"
            data-testid="copyPublishBtn"
            disabled={publishBlocked}
            onClick={onPublish}
          />
        </PermissionComponent>
      </span>
      {confirming && (
        <Modal
          disableClickout
          size="sm"
          title="Reset to the default text?"
          onRequestClose={busy ? undefined : () => setConfirming(false)}
        >
          <div
            data-testid="copyResetDialog"
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ padding: '18px 24px' }}>
              {`${label} goes back to the text in the code. The current text stays in History, so you can revert.`}
              {hasDraft && ' The saved draft is discarded too.'}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 10,
                padding: '14px 24px',
                borderTop: '1px solid #EDEDED',
              }}
            >
              <Button
                a11yLabel="Cancel"
                label="Cancel"
                appearance="outline"
                disabled={busy}
                onClick={() => setConfirming(false)}
              />
              <Button
                a11yLabel="Reset"
                label="Reset"
                appearance="primary"
                data-testid="copyResetConfirmBtn"
                disabled={busy}
                isLoading={busy}
                loadingLabel="Resetting..."
                onClick={confirmReset}
              />
            </div>
          </div>
        </Modal>
      )}
      {confirmDiscard && (
        <ConfirmationModal
          disableClickout
          title="Discard the saved draft?"
          message={`The draft of ${label} is deleted for everyone. Drafts are not kept in History.${dirty ? ' Your unsaved edits are lost too.' : ''}`}
          subMessage={null}
          confirmLabel="Discard"
          cancelLabel="Keep the draft"
          onCancel={() => setConfirmDiscard(false)}
          onConfirm={() => {
            setConfirmDiscard(false);
            onDiscard();
          }}
        />
      )}
      {guard && (
        <ConfirmationModal
          disableClickout
          title="Discard unsaved changes?"
          message="Your edits to this message are not saved."
          subMessage={null}
          confirmLabel="Discard"
          cancelLabel="Keep editing"
          onCancel={() => setGuard(null)}
          onConfirm={passGuard}
        />
      )}
    </Footer>
  );
};

export default CopyActions;
